/**
 * THE BOOKS — what we made, what we spent, who owes us, and a chat.
 *
 * Anthony, 2026-10-07: "add this for me inside of shop.supercanic.com
 * everything else. Me and Alaina only can see this. Easy to navigate and
 * understand, and I can chat to ask."
 *
 * Every figure here was built by Kubera, the read-only bookkeeper on
 * Anthony's Mac, and published to the desk (server/routes/books.ts). This
 * page does no arithmetic of its own beyond adding a bar. It shows each
 * number with how sure Kubera is of it, because the one mistake money
 * pages make is a guess that reads like a fact.
 *
 * FIVE TABS AND A CHAT. The tabs are the five questions an owner asks:
 * how is this month, how has every month gone, who owes us, what did we
 * spend, and what is missing. The chat sits under every tab so a question
 * never needs a trip.
 *
 * TWO PEOPLE. src/domain/books.ts holds the list; the server checks it
 * too, so anybody else gets "not found" from the endpoint as well.
 */

import { db } from "../data/client.ts";
import { esc, mount } from "../ui/dom.ts";
import { canSeeBooks } from "../domain/books.ts";

type Conf = "recorded" | "estimated" | "unknown";
type Line = { label: string; amount: number | null; confidence: Conf; source?: string; note?: string; due_on?: string; state?: string };
type MonthRow = {
  income_recorded: number | null; booked_not_received: number | null;
  spend_recorded: number | null; spend_estimated: number | null; net_recorded: number | null;
  income_unknown: string[]; spend_unknown: string[];
};
type Report = {
  month: string; period: string; partial: boolean;
  income: { summary: Record<string, Line> };
  costs: { items: Line[]; known_total: Line };
  ar: { owed_to_us: Line[]; own_shop_open_invoices: Line[] };
  splits: { justin: Line[]; network: Line[] };
  questions: string[]; gaps: string[];
};
type Body = { month: Report; balance: Record<string, MonthRow> };
type Snap = { built_at: string; published_at: string; body: Body };

const TABS = [
  { key: "month", label: "This month" },
  { key: "months", label: "Month by month" },
  { key: "owed", label: "Who owes us" },
  { key: "spent", label: "What we spent" },
  { key: "needs", label: "What's missing" },
] as const;
type Tab = (typeof TABS)[number]["key"];

const usd = (n: number | null | undefined, cents = false): string =>
  n == null ? "unknown"
    : n.toLocaleString("en-US", { style: "currency", currency: "USD",
      minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });

const conf = (c: Conf): string =>
  `<span class="bk-conf bk-${c}" title="${c === "recorded" ? "A system holds this number"
    : c === "estimated" ? "Worked out, not read from a statement" : "No record of this anywhere yet"}">${c}</span>`;

const monthName = (ym: string): string => {
  const [y = 2026, m = 1] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "short", year: "numeric" });
};

async function token(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

/** Tiny, safe markdown for the chat: escape first, then bold, bullets, tables. */
function md(text: string): string {
  const inline = (s: string) => esc(s).replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
  const out: string[] = [];
  const lines = text.split("\n");
  const at = (n: number): string => lines[n] ?? "";
  for (let i = 0; i < lines.length; i++) {
    const l = at(i);
    if (/^\s*\|/.test(l)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(at(i))) {
        const cells = at(i).trim().replace(/^\||\|$/g, "").split("|").map((x) => x.trim());
        if (!cells.every((x) => /^:?-+:?$/.test(x))) rows.push(cells);
        i++;
      }
      i--;
      out.push(`<table class="bk-table">${rows.map((r, n) =>
        `<tr>${r.map((x) => n === 0 ? `<th>${inline(x)}</th>` : `<td>${inline(x)}</td>`).join("")}</tr>`).join("")}</table>`);
    } else if (/^\s*[-*]\s+/.test(l)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(at(i))) {
        items.push(`<li>${inline(at(i).replace(/^\s*[-*]\s+/, ""))}</li>`); i++;
      }
      i--;
      out.push(`<ul>${items.join("")}</ul>`);
    } else if (/^#{1,4}\s/.test(l)) {
      out.push(`<p><b>${inline(l.replace(/^#+\s/, ""))}</b></p>`);
    } else if (l.trim()) {
      out.push(`<p>${inline(l)}</p>`);
    }
  }
  return out.join("");
}

export async function booksPage(profileId: string | null): Promise<void> {
  if (!canSeeBooks(profileId)) {
    mount(`<div class="page"><header class="page-head">
      <a class="back" href="#/more">&larr; Everything else</a><h1>Not found</h1></header></div>`);
    return;
  }

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Books</h1>
      <p>What we made, what we spent, and who owes us. Only you and Alaina see this page.</p></header>
    <div id="bk-body"><div class="loading">Reading the books&hellip;</div></div>
    <div class="card bk-chat">
      <div class="card-head">Ask Kubera</div>
      <div class="pad">
        <div id="bk-log" class="bk-log" aria-live="polite">
          <p class="quiet">Ask anything about the money. For example:</p>
          <div class="bk-chips">
            ${["How did September go?", "Who owes us right now?", "What is PPI bringing in?",
               "What do you need from me to finish the books?"].map((q) =>
              `<button type="button" class="bk-chip" data-q="${esc(q)}">${esc(q)}</button>`).join("")}
          </div>
        </div>
        <form id="bk-ask" class="bk-ask">
          <input id="bk-q" type="text" autocomplete="off" maxlength="1000"
            placeholder="Ask about the books&hellip;" aria-label="Ask Kubera a question">
          <button class="btn" type="submit">Ask</button>
        </form>
      </div>
    </div>
  </div>`);

  wireChat();

  const host = document.getElementById("bk-body");
  if (!host) return;

  let snap: Snap | { empty: true } | { error: string };
  try {
    const r = await fetch("/api/books", { headers: { Authorization: `Bearer ${await token()}` } });
    snap = await r.json();
  } catch {
    snap = { error: "Could not reach the desk. Check the connection and reload." };
  }
  if ("error" in snap) { host.innerHTML = `<div class="card"><div class="empty bad">${esc(snap.error)}</div></div>`; return; }
  if ("empty" in snap) { host.innerHTML = `<div class="card"><div class="empty">Kubera has not published any books yet.</div></div>`; return; }

  const s = snap;
  let tab: Tab = "month";
  try { const t = sessionStorage.getItem("bk-tab") as Tab | null; if (t && TABS.some((x) => x.key === t)) tab = t; } catch { /* private */ }

  const draw = (): void => {
    host.innerHTML = `
      <div class="bk-tabs" role="tablist">
        ${TABS.map((t) => `<button type="button" role="tab" class="bk-tab${t.key === tab ? " on" : ""}"
          aria-selected="${t.key === tab}" data-tab="${t.key}">${esc(t.label)}</button>`).join("")}
      </div>
      <p class="bk-asof">Built ${esc(new Date(s.built_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }))}.
        ${conf("recorded")} a system holds it &nbsp; ${conf("estimated")} worked out &nbsp; ${conf("unknown")} no record yet</p>
      ${tab === "month" ? thisMonth(s.body.month)
        : tab === "months" ? byMonth(s.body.balance)
        : tab === "owed" ? owed(s.body.month)
        : tab === "spent" ? spent(s.body.month)
        : missing(s.body.month)}`;
    host.querySelectorAll<HTMLButtonElement>(".bk-tab").forEach((b) => b.addEventListener("click", () => {
      tab = b.dataset["tab"] as Tab;
      try { sessionStorage.setItem("bk-tab", tab); } catch { /* private */ }
      draw();
    }));
  };
  draw();
}

function tile(label: string, l: Line | undefined, hint = ""): string {
  if (!l) return "";
  return `<div class="tile bk-tile">
    <div class="tile-n">${esc(usd(l.amount))}</div>
    <div class="tile-l">${esc(label)}</div>
    <div class="bk-tile-f">${conf(l.confidence)}${hint ? ` <span class="quiet">${esc(hint)}</span>` : ""}</div>
  </div>`;
}

function rows(lines: Line[], showDue = false): string {
  if (!lines.length) return `<div class="empty">Nothing here.</div>`;
  return lines.map((l) => `<div class="row">
    <div class="row-main">
      <div class="row-title">${esc(l.label)}</div>
      ${l.note || l.state ? `<div class="row-sub">${esc(showDue && l.state ? l.state + (l.note ? ". " + l.note : "") : l.note ?? "")}</div>` : ""}
    </div>
    <div class="row-side bk-amt"><b>${esc(usd(l.amount, true))}</b>${conf(l.confidence)}</div>
  </div>`).join("");
}

function thisMonth(r: Report): string {
  const m = r.income.summary;
  return `
    <div class="card"><div class="card-head">${esc(monthName(r.month))}${r.partial ? " so far" : ""} &middot; ${esc(r.period)}</div>
      <div class="pad">
        <p class="bk-lead">The Supercanic shops took in <b>${esc(usd(m.own_shop?.amount))}</b> this month,
          and pre-purchase inspections invoiced <b>${esc(usd(m.ppi?.amount))}</b>.
          Clickflame retainers say <b>${esc(usd(m.retainers_booked?.amount))}</b> is owed each month,
          but no system records which of it arrived.</p>
      </div>
    </div>
    <div class="tiles">
      ${tile("Shop takings (gross)", m.own_shop, "before pay, parts, tax")}
      ${tile("PPI invoiced", m.ppi)}
      ${tile("Retainers owed to us", m.retainers_booked, "booked, not paid")}
      ${tile("Retainers received", m.retainers_received)}
      ${tile("Shop Desk subscriptions", m.shop_desk_scheduled, "scheduled")}
      ${tile("MechanicRank sales", m.mechanicrank)}
      ${tile("Known costs", r.costs.known_total, "a floor")}
    </div>
    ${m.own_shop && (m.own_shop as Line & { by_method?: Record<string, number> }).by_method ? `
    <div class="card"><div class="card-head">How the shops got paid</div>
      ${rows(Object.entries((m.own_shop as Line & { by_method: Record<string, number> }).by_method)
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => ({ label: (k[0] ?? "").toUpperCase() + k.slice(1), amount: v, confidence: "recorded" as Conf })))}
    </div>` : ""}
    <div class="card"><div class="card-head">Partner splits</div>
      ${rows([...r.splits.justin, ...r.splits.network])}</div>`;
}

function byMonth(b: Record<string, MonthRow>): string {
  const months = Object.keys(b).sort().reverse();
  const top = Math.max(1, ...months.map((k) => b[k]?.income_recorded ?? 0));
  const year = (y: string) => months.filter((k) => k.startsWith(y))
    .reduce((t, k) => t + (b[k]?.income_recorded ?? 0), 0);
  return `
    <div class="card"><div class="pad">
      <p class="bk-lead">Shop takings recorded in Shop Desk: <b>${esc(usd(year("2025")))}</b> in 2025 (from August)
        and <b>${esc(usd(year("2026")))}</b> in 2026 so far. The green bar is money in. The orange bar is spending
        that has a record, which is only part of what was spent, so the gap between them is <b>not profit</b>.</p>
    </div></div>
    <div class="card"><div class="card-head">Every month</div>
      ${months.map((k) => {
        const m = b[k];
        if (!m) return "";
        const inc = m.income_recorded ?? 0, sp = m.spend_recorded ?? 0;
        return `<div class="row">
          <div class="row-main">
            <div class="row-title">${esc(monthName(k))}</div>
            <div class="bk-bars">
              <span class="bk-bar in" style="width:${(inc / top * 100).toFixed(1)}%"></span>
              <span class="bk-bar out" style="width:${(sp / top * 100).toFixed(1)}%"></span>
            </div>
            <div class="row-sub">In ${esc(usd(inc))} &middot; spent on record ${m.spend_recorded == null ? "none on file" : esc(usd(sp))}${
              m.booked_not_received ? ` &middot; booked, not proven paid ${esc(usd(m.booked_not_received))}` : ""}</div>
          </div>
        </div>`;
      }).join("")}
    </div>`;
}

function owed(r: Report): string {
  const total = r.ar.owed_to_us.reduce((t, l) => t + (l.amount ?? 0), 0)
    + r.ar.own_shop_open_invoices.reduce((t, l) => t + (l.amount ?? 0), 0);
  return `
    <div class="card"><div class="pad"><p class="bk-lead">About <b>${esc(usd(total))}</b> is on the list below.
      "No receipt on record" means nothing in our systems shows it paid. It may still have been paid by
      Zelle or check, so check the bank before chasing anyone.</p></div></div>
    <div class="card"><div class="card-head">Clients and software</div>${rows(r.ar.owed_to_us, true)}</div>
    <div class="card"><div class="card-head">Open Supercanic invoices</div>${rows(r.ar.own_shop_open_invoices, true)}</div>`;
}

function spent(r: Report): string {
  const known = r.costs.items.filter((l) => l.amount != null);
  const unknown = r.costs.items.filter((l) => l.amount == null);
  return `
    <div class="card"><div class="pad"><p class="bk-lead">Costs we can see this month add up to
      <b>${esc(usd(r.costs.known_total.amount, true))}</b>. ${unknown.length} vendors have no bill on file,
      so the real number is higher.</p></div></div>
    <div class="card"><div class="card-head">Costs with a number</div>${rows(known)}</div>
    <div class="card"><div class="card-head">Vendors with no bill on file</div>
      ${unknown.map((l) => `<div class="row"><div class="row-main"><div class="row-title">${esc(l.label)}</div>
        ${l.note ? `<div class="row-sub">${esc(l.note)}</div>` : ""}</div>
        <div class="row-side">${conf("unknown")}</div></div>`).join("")}</div>`;
}

function missing(r: Report): string {
  return `
    <div class="card"><div class="pad"><p class="bk-lead">These are the questions only you can answer,
      and the records that would turn "unknown" into real numbers. Each one makes the books more complete.</p></div></div>
    <div class="card"><div class="card-head">Questions for you</div>
      <div class="pad"><ol class="bk-list">${r.questions.map((q) => `<li>${esc(q)}</li>`).join("")}</ol></div></div>
    <div class="card"><div class="card-head">What would finish the books</div>
      <div class="pad"><ul class="bk-list">${r.gaps.map((g) => `<li>${esc(g)}</li>`).join("")}</ul></div></div>`;
}

function wireChat(): void {
  const form = document.getElementById("bk-ask") as HTMLFormElement | null;
  const input = document.getElementById("bk-q") as HTMLInputElement | null;
  const log = document.getElementById("bk-log");
  if (!form || !input || !log) return;
  const history: Array<{ role: "user" | "assistant"; content: string }> = [];
  let busy = false;

  const add = (who: "user" | "assistant" | "error", html: string): HTMLElement => {
    const el = document.createElement("div");
    el.className = `bk-msg ${who}`;
    el.innerHTML = html;
    log.appendChild(el);
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return el;
  };

  const ask = async (q: string): Promise<void> => {
    q = q.trim();
    if (!q || busy) return;
    busy = true;
    log.querySelector(".bk-chips")?.remove();
    log.querySelector(".quiet")?.remove();
    add("user", esc(q));
    history.push({ role: "user", content: q });
    const wait = add("assistant", `<span class="quiet">Kubera is reading the books&hellip;</span>`);
    input.value = "";
    try {
      const r = await fetch("/api/books/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${await token()}` },
        body: JSON.stringify({ messages: history }),
      });
      const j = await r.json() as { answer?: string; error?: string };
      if (!r.ok || !j.answer) throw new Error(j.error ?? "Kubera did not answer.");
      wait.innerHTML = md(j.answer);
      history.push({ role: "assistant", content: j.answer });
    } catch (e) {
      history.pop();
      wait.className = "bk-msg error";
      wait.textContent = e instanceof Error ? e.message : "Kubera did not answer.";
    } finally {
      busy = false;
      input.focus();
    }
  };

  form.addEventListener("submit", (e) => { e.preventDefault(); void ask(input.value); });
  log.querySelectorAll<HTMLButtonElement>(".bk-chip").forEach((b) =>
    b.addEventListener("click", () => void ask(b.dataset["q"] ?? "")));
}
